import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, Package, Eye, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const ProdAdminPanel = () => {
  const [comprovantes, setComprovantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComprovante, setSelectedComprovante] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  // URL do backend de produção
  const PROD_BACKEND_URL = "https://cheatforge.emergent.host";

  useEffect(() => {
    loadComprovantes();
    // Auto refresh every 30 seconds
    const interval = setInterval(() => {
      loadComprovantes();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadComprovantes = async () => {
    try {
      const response = await fetch(`${PROD_BACKEND_URL}/api/admin/comprovantes`);
      const data = await response.json();
      setComprovantes(data);
    } catch (error) {
      console.error('Failed to load comprovantes:', error);
      toast({
        title: '❌ Erro de conexão',
        description: 'Não foi possível carregar os comprovantes de produção',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === comprovantes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(comprovantes.map(c => c.id));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast({
        title: '⚠️ Nenhum selecionado',
        description: 'Selecione comprovantes para deletar',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedIds.length} comprovante(s) de PRODUÇÃO?`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${PROD_BACKEND_URL}/api/admin/comprovantes/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erro ao deletar');
      }
      
      toast({
        title: '✅ Deletado com sucesso!',
        description: `${result.deleted_count} comprovante(s) deletado(s) da PRODUÇÃO`,
      });
      
      setSelectedIds([]);
      loadComprovantes();
    } catch (error) {
      toast({
        title: '❌ Erro',
        description: error.message || 'Falha ao deletar comprovantes',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllComprovantes = async () => {
    const confirmMessage = `⚠️ ATENÇÃO! Você está prestes a deletar TODOS os ${comprovantes.length} comprovantes da PRODUÇÃO!

Esta ação é IRREVERSÍVEL e irá:
- Deletar todos os comprovantes de pagamento do site PRINCIPAL
- Limpar o histórico de vendas da PRODUÇÃO
- Não pode ser desfeita

Digite "DELETAR TUDO PRODUCAO" para confirmar:`;

    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'DELETAR TUDO PRODUCAO') {
      toast({
        title: '✋ Cancelado',
        description: 'Operação cancelada pelo usuário',
      });
      return;
    }

    setDeleting(true);
    try {
      // Primeiro, selecionar todos os IDs
      const allIds = comprovantes.map(comp => comp.id);
      
      if (allIds.length === 0) {
        toast({
          title: '✅ Já está limpo!',
          description: 'Não há comprovantes para deletar',
        });
        setDeleting(false);
        return;
      }

      // Deletar usando o endpoint de delete por IDs
      const response = await fetch(`${PROD_BACKEND_URL}/api/admin/comprovantes/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: allIds })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erro ao deletar todos');
      }
      
      toast({
        title: '✅ PRODUÇÃO LIMPA!',
        description: `${result.deleted_count} comprovantes da PRODUÇÃO deletados`,
      });
      
      setSelectedIds([]);
      loadComprovantes();
    } catch (error) {
      toast({
        title: '❌ Erro',
        description: error.message || 'Falha ao limpar comprovantes da produção',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const openModal = (comprovante) => {
    setSelectedComprovante(comprovante);
    setShowModal(true);
  };

  const processComprovante = async (comprovanteId) => {
    if (!window.confirm('Tem certeza que deseja APROVAR este pedido? O cliente receberá o produto.')) {
      return;
    }

    try {
      const response = await fetch(`${PROD_BACKEND_URL}/api/admin/comprovantes/${comprovanteId}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao processar comprovante');
      }
      
      toast({
        title: '✅ Pedido Aprovado!',
        description: 'Comprovante processado com sucesso',
      });
      
      // Atualizar listas
      loadComprovantes();
      setShowModal(false);
    } catch (error) {
      toast({
        title: '❌ Erro',
        description: error.message || 'Falha ao aprovar pedido',
        variant: 'destructive'
      });
    }
  };

  const pendingCount = comprovantes.filter(c => !c.processed).length;
  const processedCount = comprovantes.filter(c => c.processed).length;
  const totalValue = comprovantes.reduce((sum, c) => sum + (c.total || 0), 0);

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 gaming-title glow-red">
            🏭 PAINEL ADMIN PRODUÇÃO
          </h1>
          <p className="text-xl text-gray-400">
            Gerenciamento de comprovantes do site principal • cheatforge.emergent.host
          </p>
          <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 font-semibold">
              ⚠️ CUIDADO: Este painel gerencia dados da PRODUÇÃO (site principal)
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-yellow-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-white gaming-title">{pendingCount}</p>
              <p className="text-gray-400 text-sm">Pendentes</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-white gaming-title">{processedCount}</p>
              <p className="text-gray-400 text-sm">Processados</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-white gaming-title">{comprovantes.length}</p>
              <p className="text-gray-400 text-sm">Total</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-red-600 font-bold text-lg">R$</span>
              </div>
              <p className="text-2xl font-bold text-white gaming-title">R$ {totalValue.toFixed(2)}</p>
              <p className="text-gray-400 text-sm">Total Vendas</p>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <Card className="bg-gray-900 border-2 border-red-900/30 mb-8">
          <CardHeader className="border-b border-red-900/30">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white gaming-title">COMPROVANTES PRODUÇÃO</CardTitle>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedIds.length > 0 && `${selectedIds.length} selecionado(s) • `}
                  Total: {comprovantes.length} comprovantes
                </p>
              </div>
              <div className="flex gap-3">
                {selectedIds.length > 0 && (
                  <Button
                    onClick={deleteSelected}
                    disabled={deleting}
                    className="bg-red-700 hover:bg-red-800 text-white gaming-title border-2 border-red-600"
                  >
                    {deleting ? '⏳ DELETANDO...' : `🗑️ DELETAR (${selectedIds.length})`}
                  </Button>
                )}
                <Button
                  onClick={deleteAllComprovantes}
                  disabled={deleting}
                  className="bg-red-900 hover:bg-red-950 text-white gaming-title border-2 border-red-700"
                >
                  {deleting ? '⏳ LIMPANDO...' : '🧹 LIMPAR PRODUÇÃO'}
                </Button>
                <Button
                  onClick={loadComprovantes}
                  className="bg-red-600 hover:bg-red-700 text-white gaming-title"
                >
                  🔄 ATUALIZAR
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Comprovantes List */}
        <Card className="bg-gray-900 border-2 border-red-900/30">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Carregando comprovantes da produção...</p>
              </div>
            ) : comprovantes.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Nenhum comprovante na produção</h3>
                <p className="text-gray-400 text-sm">
                  Os comprovantes enviados pelos clientes aparecerão aqui
                </p>
              </div>
            ) : (
              <div className="space-y-4 p-6">
                {/* Select All */}
                <div className="flex items-center gap-3 pb-4 border-b border-red-900/20">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === comprovantes.length}
                    onChange={toggleSelectAll}
                    className="w-5 h-5 rounded border-red-600"
                  />
                  <span className="text-gray-400 text-sm">Selecionar todos</span>
                </div>

                {comprovantes.map((comp) => (
                  <div
                    key={comp.id}
                    className={`bg-gray-800 rounded-lg p-6 border transition-all cursor-pointer hover:border-red-600/50 ${
                      selectedIds.includes(comp.id) ? 'border-red-600' : 'border-red-900/30'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(comp.id)}
                        onChange={() => toggleSelect(comp.id)}
                        className="w-5 h-5 rounded border-red-600 mt-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={comp.processed ? 'bg-green-600' : 'bg-yellow-600'}>
                            {comp.processed ? '✓ Processado' : '⏳ Pendente'}
                          </Badge>
                          <span className="text-gray-500 text-sm">
                            {new Date(comp.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-400 mb-2">Produtos:</p>
                            {comp.cart_items?.map((item, idx) => (
                              <p key={idx} className="text-white">
                                • {item.productName} - {item.planType}
                              </p>
                            ))}
                          </div>
                          <div>
                            <p className="text-gray-400 mb-2">Contato:</p>
                            {comp.customer_email && (
                              <p className="text-white text-xs mb-1">
                                📧 {comp.customer_email}
                              </p>
                            )}
                            {comp.customer_whatsapp && (
                              <div className="flex items-center gap-2">
                                <p className="text-white text-xs">
                                  📱 {comp.customer_whatsapp}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const phone = comp.customer_whatsapp.replace(/\D/g, '');
                                    window.open(`https://wa.me/${phone}`, '_blank');
                                  }}
                                  className="text-green-500 hover:text-green-400 text-xs underline"
                                >
                                  WhatsApp
                                </button>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-400 mb-2">Valor Total:</p>
                            <p className="text-2xl font-bold text-red-600 gaming-title">
                              R$ {comp.total?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(comp);
                        }}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-600 hover:bg-red-600/20"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        VER
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal for viewing comprovante */}
        {showModal && selectedComprovante && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <Card className="bg-gray-900 border-2 border-red-900/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader className="border-b border-red-900/30 flex flex-row items-center justify-between">
                <CardTitle className="text-white gaming-title">DETALHES DO COMPROVANTE</CardTitle>
                <Button
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  size="sm"
                  className="border-red-600 text-red-600 hover:bg-red-600/20"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-6">
                {/* File Image */}
                {selectedComprovante.file_data && (
                  <div className="mb-6 text-center">
                    <img
                      src={`data:${selectedComprovante.content_type};base64,${selectedComprovante.file_data}`}
                      alt="Comprovante"
                      className="max-w-full max-h-96 rounded-lg border-2 border-red-900/30 mx-auto"
                    />
                  </div>
                )}

                {/* Details */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-gray-400 mb-2">Produtos Comprados:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20">
                      {selectedComprovante.cart_items?.map((item, idx) => (
                        <div key={idx} className="mb-3 last:mb-0">
                          <p className="text-white font-semibold">{item.productName}</p>
                          <p className="text-gray-400 text-sm">Plano: {item.planType}</p>
                          <p className="text-red-600 font-bold">R$ {item.price?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-400 mb-2">Contato do Cliente:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20 space-y-3">
                      {selectedComprovante.customer_email && (
                        <div>
                          <p className="text-gray-500 text-sm">Email para entrega</p>
                          <p className="text-white break-all">
                            📧 {selectedComprovante.customer_email}
                          </p>
                        </div>
                      )}
                      {selectedComprovante.customer_whatsapp && (
                        <div>
                          <p className="text-gray-500 text-sm">WhatsApp</p>
                          <div className="flex items-center gap-3">
                            <p className="text-white">
                              📱 {selectedComprovante.customer_whatsapp}
                            </p>
                            <Button
                              onClick={() => {
                                const phone = selectedComprovante.customer_whatsapp.replace(/\D/g, '');
                                window.open(`https://wa.me/${phone}`, '_blank');
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                            >
                              Abrir WhatsApp
                            </Button>
                          </div>
                        </div>
                      )}
                      {!selectedComprovante.customer_email && !selectedComprovante.customer_whatsapp && (
                        <p className="text-gray-500 text-sm italic">
                          Informações de contato não disponíveis (comprovante antigo)
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-400 mb-2">Informações do Pedido:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20 space-y-3">
                      <div>
                        <p className="text-gray-500 text-sm">Data de Envio</p>
                        <p className="text-white">
                          {new Date(selectedComprovante.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Valor Total</p>
                        <p className="text-3xl font-bold text-red-600 gaming-title">
                          R$ {selectedComprovante.total?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Status</p>
                        <Badge className={selectedComprovante.processed ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedComprovante.processed ? '✓ Processado' : '⏳ Pendente'}
                        </Badge>
                      </div>
                      
                      {/* Botão para aprovar/processar */}
                      {!selectedComprovante.processed && (
                        <div className="pt-3 border-t border-red-900/30">
                          <Button
                            onClick={() => processComprovante(selectedComprovante.id)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                          >
                            ✅ APROVAR PEDIDO
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProdAdminPanel;